import { useRef, useState } from 'react';
import { AttendanceInput, Registration, UpdateRegistrationInput } from '../API';
import { getParty, saveAttendance } from '../graphql/partyAttendance';
import { attendanceCommand } from '../utils/partyAttendance';

export function usePartyAttendance(party: Registration, onChange: (party: Registration) => void) {
  // Survives a scanner/list remount or page refresh; no attendee names or credentials are stored.
  const key = `eventhub-attendance:${localStorage.getItem('kc_email')}:${party.eventId}:${party.registrationId}`;
  const [pending, setPending] = useState<UpdateRegistrationInput | null>(() => {
    try {
      const input = JSON.parse(sessionStorage.getItem(key) ?? 'null') as UpdateRegistrationInput | null;
      return input?.eventId === party.eventId && input?.registrationId === party.registrationId && input?.attendance && input?.requestId ? input : null;
    } catch { return null; }
  });
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remember = (input: UpdateRegistrationInput | null) => {
    if (input) sessionStorage.setItem(key, JSON.stringify(input));
    else sessionStorage.removeItem(key);
    setPending(input);
  };
  const send = async (input: UpdateRegistrationInput) => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      // Persist before sending so an interrupted response can be retried with the identical ID/input.
      remember(input);
      onChange(await saveAttendance(input));
      remember(null);
      // A replay returns its historical receipt. Read again to pick up later staff changes.
      try { const fresh = await getParty(party.eventId, party.registrationId); if (fresh) onChange(fresh); }
      catch { setError('Attendance saved. Refresh to load the latest party.'); }
      return true;
    } catch (caught) {
      const failure = caught as { graphQLErrors?: { message: string; errorType?: string; extensions?: { errorType?: string } }[] };
      const errors = failure.graphQLErrors ?? [];
      const settled = ['Unauthorized', 'ValidationError', 'ROSTER_CHANGED', 'ATTENDEE_NOT_ACTIVE', 'EVENT_CLOSED', 'EVENT_UNAVAILABLE', 'REGISTRATION_UNAVAILABLE', 'NAMED_WRITES_DISABLED', 'IDEMPOTENCY_MISMATCH', 'INVALID_PARTY_SNAPSHOT', 'CONVERSION_REQUIRED', 'PARTY_TOO_LARGE'];
      const uncertain = !errors.length || errors.some(e => !settled.includes(e.errorType ?? e.extensions?.errorType ?? ''));
      if (!uncertain) remember(null);
      setError(uncertain ? 'The result is uncertain. Retry the same action before making another change.'
        : `${errors[0]?.message ?? 'Attendance could not be saved.'} Refresh and review the party before trying again.`);
      try { const fresh = await getParty(party.eventId, party.registrationId); if (fresh) onChange(fresh); } catch { /* Keep the actionable command error. */ }
      return false;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const run = async (intent: AttendanceInput): Promise<boolean> => {
    if (pending || inFlight.current) return false;
    try { return send(attendanceCommand(party, intent, crypto.randomUUID())); }
    catch (caught) { setError((caught as Error).message); return false; }
  };
  return { busy, pending, error, run, retry: () => pending && send(pending) };
}
